
import { useState, useEffect } from "react";
import { baseUrl } from "../../config";
import moment from 'moment';
import axios from "axios";
import { 
  Row, 
  Col, 
  Button, 
  Space, 
  notification, 
  Form, 
  Input, 
  DatePicker, 
  Table, 
  Modal,
  Card,
  Typography,
  Divider,
  Empty,
  Tooltip,
  Badge,
  Alert,
  Spin,
  Tag,
  AutoComplete
} from "antd";
import '../../assets/styles/custom_style.css';
import { 
  CalendarOutlined, 
  DeleteOutlined, 
  EditOutlined,
  BankOutlined,
  TeamOutlined,
  PlusOutlined,
  TableOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SaveOutlined,
  ClearOutlined,
  TrophyOutlined,
  StarOutlined,
  ClockCircleOutlined,
  GlobalOutlined
} from "@ant-design/icons";

const { Item } = Form;
const { confirm } = Modal;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SocialStatus = ({ user_id }) => {
  console.log("user_id for social status:", user_id);
  
  const [form] = Form.useForm();
  const [recordId, setRecordId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [optionSuggestion, setOptionSuggestion] = useState([]);
  
  // Enhanced state management
  const [viewMode, setViewMode] = useState('table'); // 'table', 'form', 'empty'
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    setUserData([]);
    setViewMode('empty');
    setLoading(true);
    
    if (user_id) {
      fetchSocialStatusData(user_id);
    } else {
      setLoading(false);
    }
  }, [user_id]);

  const fetchSocialStatusData = async (user_id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/social-status/list/${user_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      });
      
      if (response.data.status) {
        const socialData = response.data.result || [];
        setUserData(socialData);
        setViewMode(socialData.length > 0 ? 'table' : 'empty');
      } else {
        notification.info({
          message: 'Info',
          description: response.data.message,
          placement: 'topRight'
        });
        setViewMode('empty');
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to fetch social status data',
        placement: 'topRight'
      });
      setViewMode('empty');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    // Validate user_id
    if (!user_id) {
      notification.warning({
        message: 'Warning',
        description: "Please save member basic information first before adding social status.",
        placement: 'topRight'
      });
      return;
    }

    // Check if at least one field is filled
    const isEmpty = Object.values(values).every(x => x === null || x === '' || x === undefined);
    if (isEmpty) {
      notification.info({
        message: 'Info',
        description: "Please fill out at least one field.",
        placement: 'topRight'
      });
      return;
    }

    setSaving(true);
    try {
      values.user_id = user_id;

      // Format dates if present
      if (values.sn_duration_start) {
        values.sn_duration_start = moment(values.sn_duration_start).format('YYYY-MM-DD');
      }
      if (values.sn_duration_end) {
        values.sn_duration_end = moment(values.sn_duration_end).format('YYYY-MM-DD');
      }

      let response;
      if (isUpdateMode) {
        response = await axios.patch(`${baseUrl}/social-status/update/${recordId}`, values, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      } else {
        response = await axios.post(`${baseUrl}/social-status/create`, values, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      }

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode 
            ? 'Social status updated successfully!' 
            : 'Social status added successfully!',
          placement: 'topRight'
        });
        
        // Reset form and refresh data
        handleFormReset();
        await fetchSocialStatusData(user_id);
        setViewMode('table');
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to save social status',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'An error occurred while saving',
        placement: 'topRight'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    // Populate form with record data
    form.setFieldsValue({
      sn_organisation_name: record.sn_organisation_name || '',
      sn_post: record.sn_post || '',
      sn_duration_start: record.sn_duration_start ? moment(record.sn_duration_start) : null,
      sn_duration_end: record.sn_duration_end ? moment(record.sn_duration_end) : null,
      sn_organisation_address: record.sn_organisation_address || '',
      org_phone_no: record.org_phone_no || '',
    });
    
    setRecordId(record.id);
    setSelectedRecord(record);
    setIsUpdateMode(true);
    setViewMode('form');
  };

  const handleDelete = async (record) => {
    try {
      const response = await axios.delete(`${baseUrl}/social-status/delete/${record.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: 'Social status record deleted successfully!',
          placement: 'topRight'
        });
        await fetchSocialStatusData(user_id);
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to delete record',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to delete record',
        placement: 'topRight'
      });
    }
  };

  const showDeleteConfirm = (record) => {
    confirm({
      title: 'Delete Social Status Record',
      content: `Are you sure you want to delete the record for "${record.sn_organisation_name}"? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(record);
      },
    });
  };

  const handleFormReset = () => {
    form.resetFields();
    setRecordId(null);
    setSelectedRecord(null);
    setIsUpdateMode(false);
  };

  const handleViewChange = (newView) => {
    if (newView === 'form') {
      handleFormReset();
    }
    setViewMode(newView);
  };

  const getPanelValue = async (searchText, columnName, tableName) => {
    if (searchText.length < 3) {
      setOptionSuggestion([]);
      return;
    }

    try {
      const response = await axios.get(`${baseUrl}/payments/getSuggestionValue/`, {
        params: { q: searchText, column: columnName, table: tableName },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        setOptionSuggestion(response.data.result);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleChangeSuggestion = (value, columnName) => {
    form.setFieldsValue({
      [columnName]: value
    });
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return null;
    const start = moment(startDate);
    const end = endDate ? moment(endDate) : moment();
    const duration = moment.duration(end.diff(start));
    const years = Math.floor(duration.asYears());
    const months = Math.floor(duration.asMonths()) % 12;
    
    if (years > 0) {
      return `${years}y ${months}m`;
    } else {
      return `${months}m`;
    }
  };

  const getStatusColor = (startDate, endDate) => {
    if (!endDate) return '#52c41a'; // Active (green)
    if (moment().isAfter(moment(endDate))) return '#ff4d4f'; // Completed (red)
    return '#1890ff'; // Ongoing (blue)
  };

  const getStatusText = (startDate, endDate) => {
    if (!endDate) return 'Current';
    if (moment().isAfter(moment(endDate))) return 'Completed';
    return 'Ongoing';
  };

  const columns = [
    {
      title: 'Organization',
      key: 'organization',
      render: (_, record) => (
        <div>
          <Text strong>{record.sn_organisation_name || '-'}</Text>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
            <TrophyOutlined /> {record.sn_post || 'Position not specified'}
          </div>
        </div>
      ),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <CalendarOutlined /> {record.sn_duration_start ? moment(record.sn_duration_start).format('MMM YYYY') : 'N/A'}
            {' - '}
            {record.sn_duration_end ? moment(record.sn_duration_end).format('MMM YYYY') : 'Present'}
          </div>
          {record.sn_duration_start && (
            <div style={{ fontSize: '10px', color: '#666', marginTop: 2 }}>
              <ClockCircleOutlined /> {calculateDuration(record.sn_duration_start, record.sn_duration_end)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={getStatusColor(record.sn_duration_start, record.sn_duration_end)}>
          {getStatusText(record.sn_duration_start, record.sn_duration_end)}
        </Tag>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.org_phone_no && (
            <div style={{ fontSize: '12px' }}>
              <PhoneOutlined /> {record.org_phone_no}
            </div>
          )}
          {record.sn_organisation_address && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
              <EnvironmentOutlined /> {record.sn_organisation_address}
            </div>
          )}
          {!record.org_phone_no && !record.sn_organisation_address && '-'}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          {/* <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => showDeleteConfirm(record)}
            />
          </Tooltip> */}

          <div className="button-container">
                     <Space size="middle">
                         <button className="update-btn" onClick={() => handleEdit(record)}> <EditOutlined /></button>
                         <button className="delete-btn" onClick={() => showDeleteConfirm(record)}><DeleteOutlined /></button>
                     </Space>
                 </div>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading social status...</div>
      </div>
    );
  }

  if (!user_id) {
    return (
      <Alert
        message="Member Required"
        description="Please save member basic information first before adding social status."
        type="warning"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <Card
        size="small"
        style={{ marginBottom: 6 }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrophyOutlined style={{ color: '#7a0000' }} />
            <span>Social Status & Positions</span>
            {userData.length > 0 && (
              <Badge count={userData.length} style={{ backgroundColor: '#52c41a' }} />
            )}
          </div>
        }
        extra={
          <Space>
            {viewMode === 'table' && userData.length > 0 && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleViewChange('form')}
                style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
              >
                Add Position
              </Button>
            )}
            {viewMode === 'form' && (
              <Button
                icon={<TableOutlined />}
                onClick={() => handleViewChange('table')}
                disabled={userData.length === 0}
                style={{ borderRadius: 8, borderColor: '#7a0000', color: '#7a0000' }}
              >
                View All
              </Button>
            )}
          </Space>
        }
      />

      {/* Content */}
      {viewMode === 'empty' && userData.length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text type="secondary">No social status records found</Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    icon={<StarOutlined />}
                    onClick={() => handleViewChange('form')}
                    style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
                  >
                    Add First Position
                  </Button>
                </div>
              </div>
            }
          />
        </Card>
      )}

      {viewMode === 'table' && userData.length > 0 && (
        <Card styles={{ body: { padding: '8px 12px' } }}>
          <Table
            columns={columns}
            dataSource={userData}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Total ${total} positions`
            }}
            scroll={{ x: true }}
            rowKey="id"
          />
        </Card>
      )}

      {viewMode === 'form' && (
        <Card
          styles={{ body: { padding: '6px 10px' } }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isUpdateMode ? <EditOutlined /> : <StarOutlined />}
              <span>{isUpdateMode ? 'Edit Social Position' : 'Add Social Position'}</span>
            </div>
          }
          extra={
            <Space>
              <Button 
                icon={<ClearOutlined />} 
                onClick={handleFormReset}
              >
                Clear
              </Button>
              {userData.length > 0 && (
                <Button 
                  icon={<TableOutlined />}
                  onClick={() => handleViewChange('table')}
                >
                  View All
                </Button>
              )}
            </Space>
          }
        >
          <Form 
            form={form} 
            onFinish={onFinish} 
            layout="vertical"
            size="small"
            className="add-payment-form compact-form"
          >
            <Row gutter={[8, 0]}>
              {/* Organization Information */}
              <Col xs={24}>
                <Divider orientation="left" style={{ margin: '4px 0' }}>
                  <BankOutlined /> Organization Details
                </Divider>
              </Col>
              
              <Col xs={24} sm={12} lg={8}>
                <Item 
                  label="Organization Name" 
                  name="sn_organisation_name"
                  rules={[{ required: true, message: 'Please enter organization name' }]}
                >
                  <AutoComplete 
                    placeholder="Enter organization name"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "sn_organisation_name")}
                    onSearch={(text) => getPanelValue(text, "sn_organisation_name", "sn_social_status")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Item 
                  label="Position/Post" 
                  name="sn_post"
                  rules={[{ required: true, message: 'Please enter position' }]}
                >
                  <AutoComplete 
                    placeholder="Enter position/post"
                    options={optionSuggestion}
                    onChange={(value) => handleChangeSuggestion(value, "sn_post")}
                    onSearch={(text) => getPanelValue(text, "sn_post", "sn_social_status")}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Item 
                  label="Organization Phone" 
                  name="org_phone_no"
                  rules={[
                    {
                      pattern: /^\d{10,12}$/,
                      message: 'Please enter a valid phone number (10-12 digits)',
                    }
                  ]}
                >
                  <Input 
                    placeholder="Organization phone number" 
                    prefix={<PhoneOutlined />}
                  />
                </Item>
              </Col>

              {/* Duration Information */}
              <Col xs={24}>
                <Divider orientation="left" style={{ margin: '4px 0' }}>
                  <CalendarOutlined /> Duration & Timeline
                </Divider>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Item 
                  label="Start Date" 
                  name="sn_duration_start"
                  rules={[{ required: true, message: 'Please select start date' }]}
                >
                  <DatePicker 
                    className="filter_dob" 
                    format="DD-MM-YYYY" 
                    placeholder="Select start date"
                    style={{ width: '100%' }}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Item label="End Date" name="sn_duration_end">
                  <DatePicker 
                    className="filter_dob" 
                    format="DD-MM-YYYY" 
                    placeholder="Select end date (optional)"
                    style={{ width: '100%' }}
                  />
                </Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <div style={{ padding: '12px 16px', background: '#f5f5f5', border: '1px solid #e8e8e8', borderRadius: '6px', marginTop: '30px' }}>
                  <div style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>
                    Tip: Leave end date empty for current/active positions
                  </div>
                </div>
              </Col>

              {/* Address Information */}
              <Col xs={24}>
                <Divider orientation="left" style={{ margin: '4px 0' }}>
                  <EnvironmentOutlined /> Location
                </Divider>
              </Col>

              <Col xs={24}>
                <Item label="Organization Address" name="sn_organisation_address">
                  <Input.TextArea 
                    placeholder="Enter organization address" 
                    rows={3}
                  />
                </Item>
              </Col>

              {/* Action Buttons */}
              <Col xs={24}>
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                  {isUpdateMode && selectedRecord && (
                    <span style={{ fontSize: 12, color: '#8c8c8c', marginRight: 'auto' }}>
                      Editing: {selectedRecord.sn_organisation_name || 'Unnamed Organization'}
                    </span>
                  )}
                  {userData.length > 0 && (
                    <Button icon={<TableOutlined />} onClick={() => handleViewChange('table')} disabled={saving} style={{ borderRadius: 8, borderColor: '#7a0000', color: '#7a0000' }}>
                      View All Positions
                    </Button>
                  )}
                  <Button icon={<ClearOutlined />} onClick={handleFormReset} disabled={saving} style={{ borderRadius: 8 }}>
                    Clear Form
                  </Button>
                  <Button type="primary" icon={saving ? null : <SaveOutlined />} loading={saving} onClick={() => form.submit()} style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8, minWidth: 130 }}>
                    {saving ? 'Saving...' : (isUpdateMode ? 'Update Position' : 'Add Position')}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      )}
    </div>
  );
};

export default SocialStatus;